import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import moment from 'moment'

export const selectNumClassrooms = (state: any) => {
  const numExampleClassrooms: number = state.classrooms.filter((classroom: any) => classroom.exampleClass).length
  return state.classrooms.length - numExampleClassrooms
}

export const selectActiveClassrooms = (state: any) => state.classrooms.filter((classroom: any) => !classroom.archived && !classroom.exampleClass)
export const selectArchivedClassrooms = (state: any) => state.classrooms.filter((classroom: any) => classroom.archived && !classroom.exampleClass)
export const selectAllClassrooms = (state: any) => state.classrooms
export const selectClassroomById = (id: number | string) => (state: any) => state.classrooms.find((classroom: any) => classroom.id === id)

const mapClassroomToState = (classroom: any) => ({
  id: classroom.id,
  archived: classroom.archived,
  name: classroom.name,
  subject: classroom.subject,
  emoji: classroom.emoji,
  numStudents: classroom.numStudents,
  numUnconfirmedStudents: classroom.numUnconfirmedStudents,
  numNotLoggedInStudents: classroom.numNotLoggedInStudents,
  startDate: classroom.startDate,
  code: classroom.code,
  exampleClass: false,
  focusSeshLogs: classroom.focusSeshLogs,
  seenPreviousCourseProgressWarning: classroom.seenPreviousCourseProgressWarning,
  lastVisitedTabIndex: classroom.lastVisitedTabIndex,

  // Custom focus sesh settings for Projected Sessions
  lastCustomFocusSeshWasTimed: classroom.lastCustomFocusSeshWasTimed,
  lastCustomFocusSeshDuration: classroom.lastCustomFocusSeshDuration,
  lastCustomFocusSeshWasNoCheckIns: classroom.lastCustomFocusSeshWasNoCheckIns,
  lastCustomFocusSeshCheckInTiming: classroom.lastCustomFocusSeshCheckInTiming,
  lastCustomFocusSeshCheckInSound: classroom.lastCustomFocusSeshCheckInSound,
  lastCustomFocusSeshSoundtrack: classroom.lastCustomFocusSeshSoundtrack,

  // For admin stats on Class Tile only
  numDailyBeats: classroom.numDailyBeats,
  numLessons: classroom.numLessons,
  percentLessonsCompleted: classroom.percentLessonsCompleted,
  percentDailyBeatsCompleted: classroom.percentDailyBeatsCompleted,
  mhePercentLessonsCompleted: classroom.mhePercentLessonsCompleted,
})

export const SEEN_PREVIOUS_COURSE_PROGRESS_WARNING = {
  NOT_APPLICABLE: 'not_applicable',
  HAS_NOT_SEEN: 'has_not_seen',
  HAS_SEEN: 'has_seen',
} as const

const exampleClasses = [
  {
    id: 'example-active-class',
    name: 'Example Active Class',
    numStudents: 4,
    numUnconfirmedStudents: 0,
    numNotLoggedInStudents: 0,
    startDate: moment().subtract(1, 'weeks').toISOString(),
    code: 'XXXX',
    exampleClass: true,
    focusSeshLogs: [
      // STUDENT A
      {
        id: 1,
        userId: 'example-active-1',
        completed: true,
        numCheckIns: 3,
        secondsDuration: 1200,
        createdAt: moment().subtract(1, 'day').toISOString(),
        updatedAt: moment().subtract(1, 'day').toISOString(),
        linkedClassroomId: 'example-active-class',
      },
      {
        id: 3,
        userId: 'example-active-1',
        completed: true,
        numCheckIns: 4,
        secondsDuration: 1200,
        createdAt: moment().toISOString(),
        updatedAt: moment().toISOString(),
        linkedClassroomId: 'example-active-class',
      },
      {
        id: 4,
        userId: 'example-active-1',
        completed: true,
        numCheckIns: 4,
        secondsDuration: 1200,
        createdAt: moment().toISOString(),
        updatedAt: moment().toISOString(),
        linkedClassroomId: 'example-active-class',
      },
      // STUDENT B
      {
        id: 2,
        userId: 'example-active-2',
        completed: true,
        numCheckIns: 4,
        secondsDuration: 1500,
        createdAt: moment().subtract(2, 'days').toISOString(),
        updatedAt: moment().subtract(2, 'days').toISOString(),
        linkedClassroomId: 'example-active-class',
      },
      {
        id: 5,
        userId: 'example-active-2',
        completed: true,
        numCheckIns: 5,
        secondsDuration: 1500,
        createdAt: moment().toISOString(),
        updatedAt: moment().toISOString(),
        linkedClassroomId: 'example-active-class',
      },
      // STUDENT C (no linked sessions)
      // STUDENT D
      {
        id: 6,
        userId: 'example-active-4',
        completed: true,
        numCheckIns: 2,
        secondsDuration: 900,
        createdAt: moment().toISOString(),
        updatedAt: moment().toISOString(),
        linkedClassroomId: 'example-active-class',
      },
      // Sessions not linked to classroom for "All Sessions" view
      {
        id: 7,
        userId: 'example-active-1',
        completed: true,
        numCheckIns: 5,
        secondsDuration: 1800,
        createdAt: moment().subtract(20, 'day').toISOString(),
        updatedAt: moment().subtract(20, 'day').toISOString(),
        linkedClassroomId: null,
      },
      {
        id: 8,
        userId: 'example-active-1',
        completed: true,
        numCheckIns: 1,
        secondsDuration: 600,
        createdAt: moment().subtract(28, 'day').toISOString(),
        updatedAt: moment().subtract(28, 'day').toISOString(),
        linkedClassroomId: null,
      },
      {
        id: 9,
        userId: 'example-active-3',
        completed: true,
        numCheckIns: 8,
        secondsDuration: 6000,
        createdAt: moment().toISOString(),
        updatedAt: moment().toISOString(),
        linkedClassroomId: null,
      },
    ],
    seenPreviousCourseProgressWarning: SEEN_PREVIOUS_COURSE_PROGRESS_WARNING.NOT_APPLICABLE,
  },
]

const initialState: any[] = []

const classroomsSlice = createSlice({
  name: 'classrooms',
  initialState,
  reducers: {
    loadClassrooms: (state, {
      payload: classrooms,
    }: PayloadAction<any[]>) => {
      const mappedClassrooms = classrooms.map((classroom: any) => mapClassroomToState(classroom))
      return exampleClasses.concat(mappedClassrooms)
    },
    updateSeenPreviousCourseProgressWarning: (state, { payload: { classroomId, warningStatus } }: PayloadAction<{ classroomId: number; warningStatus: string }>) => {
      const classroom = state.find(classroom => classroom.id === classroomId)
      if (classroom) {
        classroom.seenPreviousCourseProgressWarning = warningStatus
      }
    },
    refreshSingleClassroom: (state, { payload: classroom }: PayloadAction<any>) => {
      if (classroom) {
        return state.map(c => c.id === classroom?.id ? mapClassroomToState(classroom) : c)
      }
    },
    updateClassroomFocusSeshSettings: (state, { payload: { classroomId, seshSettings } }: PayloadAction<{ classroomId: number; seshSettings: any }>) => {
        const classroom = state.find(classroom => classroom.id === classroomId)
        if (classroom) {
            classroom.lastCustomFocusSeshWasTimed = seshSettings.isTimed
            if (seshSettings.isTimed) {
              classroom.lastCustomFocusSeshDuration = seshSettings.duration
            }
            classroom.lastCustomFocusSeshWasNoCheckIns = seshSettings.isNoCheckIns
            classroom.lastCustomFocusSeshCheckInTiming = seshSettings.checkInTiming
            classroom.lastCustomFocusSeshCheckInSound = seshSettings.checkInSound
            classroom.lastCustomFocusSeshSoundtrack = seshSettings.soundtrack
        }
    }
  },
})

export const { loadClassrooms, updateSeenPreviousCourseProgressWarning, refreshSingleClassroom, updateClassroomFocusSeshSettings } = classroomsSlice.actions

export default classroomsSlice.reducer
